import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { exportActivities } from "../../../shared/api/activities";
import { DataTable } from "../../../shared/ui/DataTable";
import {
  Button,
  HeadingText,
  PaginationControls,
  Shimmer,
  Tabs,
} from "../../../shared/ui";
import { useActivities } from "../../../shared/api/hooks/useActivities";
import { getApiErrorMessage } from "../../../shared/lib";
import { useDebouncedValue } from "../../../hooks/use-debounced-value";
import {
  getBufferedPageOffset,
  getBufferedRequestLimit,
  getBufferedRequestPage,
  getUiTotalPages,
} from "../../../shared/lib/bufferedPagination";
import { ACTIVITY_SORT_OPTIONS, ACTIVITY_TABS } from "../constants/activityData";
import { ActivityItem, ActivitySort } from "../types";
import ActivityFiltersBar from "./ActivityFiltersBar";
import { activityColumns } from "./activityColumns";
import { RootState } from "../../../redux/store";

const ActivitySection = () => {
  const pageSize = useSelector((state: RootState) => state.totalItemPerpage) || 20;
  const [activeTab, setActiveTab] = useState("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<ActivitySort>("recent");
  const [currentPage, setCurrentPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const debouncedQuery = useDebouncedValue(query, 400);
  const requestLimit = getBufferedRequestLimit(pageSize);
  const requestPage = getBufferedRequestPage(currentPage);
  const requestStatus =
    activeTab === "success"
      ? "success"
      : activeTab === "failed"
        ? "failed"
        : undefined;

  const { activities, meta, isLoading, error, refetch } = useActivities({
    ownerType: "Admin",
    q: debouncedQuery.trim() || undefined,
    status: requestStatus,
    sort,
    page: requestPage,
    limit: requestLimit,
  });

  const uiActivities = useMemo<ActivityItem[]>(() => {
    return activities.map((item) => ({
      id: item.id,
      memberName: item.memberName,
      memberProfileImage: item.memberProfileImage,
      action: item.action,
      module: item.module,
      target: item.target,
      targetId: item.targetId,
      outcome: item.status ? "Success" : "Failure",
      location: item.location,
      timestamp: item.timestamp,
    }));
  }, [activities]);

  const totalPages = getUiTotalPages(
    meta,
    pageSize,
    requestLimit,
    uiActivities.length,
  );

  const pagedActivity = useMemo(() => {
    const start = getBufferedPageOffset(currentPage, pageSize);
    return uiActivities.slice(start, start + pageSize);
  }, [currentPage, pageSize, uiActivities]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, debouncedQuery, sort]);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const handleExport = async () => {
    setIsExporting(true);

    try {
      const status =
        activeTab === "success"
          ? "Success"
          : activeTab === "failed"
            ? "Failed"
            : undefined;

      const { blob, filename } = await exportActivities({ status });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Unable to export activity logs"));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <section className="w-full">
      <HeadingText className="text-[28px] font-[450] text-[#161827]">
        Activity
      </HeadingText>

      <Tabs
        items={ACTIVITY_TABS}
        value={activeTab}
        onChange={setActiveTab}
        className="my-4"
      />

      <ActivityFiltersBar
        query={query}
        onQueryChange={setQuery}
        sort={sort}
        onSortChange={setSort}
        sortOptions={ACTIVITY_SORT_OPTIONS}
        onExport={handleExport}
        isExporting={isExporting}
      />

      {isLoading ? (
        <DataTable
          columns={activityColumns}
          data={[]}
          getRowKey={(item) => item.id}
          emptyState={
            <div className="space-y-3 py-2">
              {Array.from({ length: 5 }).map((_, index) => (
                <Shimmer key={index} className="h-8 w-full rounded-[10px]" />
              ))}
            </div>
          }
        />
      ) : error ? (
        <DataTable
          columns={activityColumns}
          data={[]}
          getRowKey={(item) => item.id}
          emptyState={
            <div className="space-y-3 py-2">
              <div className="text-[14px] text-[#C24E4E]">{error}</div>
              <Button
                type="button"
                variant="outline"
                className="h-10 rounded-[12px]"
                onClick={refetch}
              >
                Retry
              </Button>
            </div>
          }
        />
      ) : (
        <DataTable
          columns={activityColumns}
          data={pagedActivity}
          getRowKey={(item) => item.id}
        />
      )}

      {!isLoading && !error && uiActivities.length > 0 ? (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          className="mt-8 flex items-center gap-2"
        />
      ) : null}
    </section>
  );
};

export default ActivitySection;
